import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil} from 'ramda';
import {isPositive, isNonPositive} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {
  GetMetadaServiceCaseApiResponse,
  GetPostponeJobsApiResponse,
  GetServiceCaseOrderCountApiResponse,
  useGetServiceCaseByVehicleCountQuery,
} from '@dms/api/workshop';
import featureFlags from '@dms/feature-flags';
import {CustomNoticeList} from '@dms/features/custom-notices';
import {GDPRCollector} from '@dms/features/document-context';
import {TaskGridPage} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  Checklists,
  CommunicationSection,
  getCommunicationSectionTitle,
  OwnershipSection,
  Section,
  SectionItem,
  workshopSearchParams,
} from '@dms/shared';
import {TiresOverview} from '@dms/shared-aftersales';

import {buildArray, Nullish} from 'shared/web';

import {ActiveServiceCases} from '../(sections)/ActiveServiceCases/ActiveServiceCases';
import {ActivityLogs} from '../(sections)/ActivityLogs/ActivityLogs';
import {CheckinHandover} from '../(sections)/CheckinHandover/CheckinHandover';
import {ServiceCaseDocuments} from '../(sections)/Documents/ServiceCaseDocuments';
import {HistoryOfRepairs} from '../(sections)/HistoryOfRepairs/HistoryOfRepairs';
import {Orders} from '../(sections)/Order/Orders';
import {Overview} from '../(sections)/Overview/Overview';
import {Photos} from '../(sections)/Photos/Photos';
import {Postponed} from '../(sections)/PostponedRequests/Postponed';
import {VehicleCampaigns} from '../(sections)/ServiceCampaigns/VehicleCampaigns';
import {ServiceIntervals} from '../(sections)/ServiceIntervals/ServiceIntervals';
import {Vehicle} from '../(sections)/Vehicle/Vehicle';
import {useServiceCaseDetailBootstrap} from './useServiceCaseDetailBootstrap';
import {useServiceCaseSectionBadges} from './useServiceCaseSectionBadges';

interface UseServiceCaseDetailSectionsProps {
  serviceCaseId: string;
  activeSection: string;
  activeServiceCampaignsLength: number;
  canCancelServiceCase: boolean;
  canReadTasks: boolean;
  canReadServiceCase: boolean;
  canReadPneuhotel: boolean;
  serviceCase: GetMetadaServiceCaseApiResponse;
  serviceCaseOrderCount: GetServiceCaseOrderCountApiResponse;
  vehicleCustomersCount: number | Nullish;
  tireSetCount: number | Nullish;
  postponedJobs: GetPostponeJobsApiResponse;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function useServiceCaseDetailSections(props: UseServiceCaseDetailSectionsProps) {
  const isServiceCaseReadOnly = props.serviceCase?.state === 'CANCELLED';
  const maximization = {
    enabled: true,
    title: `${i18n.t('page.workshop.labels.serviceCase')} ${props.serviceCase?.number ?? ''}`,
  };

  // Deduped subscription with useServiceCaseDetailData — single settle-gate definition.
  // The counts hooks stay (their caches are seeded by the bootstrap) but wait for it to
  // settle.
  const {bootstrap, isBootstrapSettled} = useServiceCaseDetailBootstrap(props.serviceCaseId);
  const vehicleId = props.serviceCase?.vehicleId ?? bootstrap?.case?.vehicleId;

  const activeServiceCasesCountApiArg =
    isNotNil(vehicleId) && isBootstrapSettled ? {vehicleId, status: 'ACTIVE' as const} : skipToken;
  const {data: activeServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    activeServiceCasesCountApiArg
  );

  const finishedServiceCasesCountApiArg =
    isNotNil(vehicleId) && isBootstrapSettled
      ? {vehicleId, status: 'FINISHED' as const}
      : skipToken;
  const {data: finishedServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    finishedServiceCasesCountApiArg
  );

  const {
    documentCount,
    checklistBadge,
    tasksCountBadge,
    customNoticesBadge,
    activityLogsCount,
    outgoingMessagesCount,
  } = useServiceCaseSectionBadges({
    serviceCaseId: props.serviceCaseId,
    customerId: props.serviceCase?.customerId,
    activeSection: props.activeSection,
  });

  return buildArray<SectionItem>([
    {
      id: workshopSearchParams.serviceDetail.overview,
      label: i18n.t('entity.serviceCase.labels.overview'),
      content: <Overview isReadOnly={isServiceCaseReadOnly} />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-overview'),
    },
  ])
    .add({
      id: workshopSearchParams.serviceDetail.orders,
      label: i18n.t('entity.serviceCase.labels.orders'),
      content: <Orders canCancelServiceCase={props.canCancelServiceCase} />,
      badge: isPositive(props.serviceCaseOrderCount?.count)
        ? {value: String(props.serviceCaseOrderCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-orders'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.checkinHandover,
      label: i18n.t('entity.workshop.labels.checkinAndHandover'),
      content: (
        <CheckinHandover
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          isReadOnly={isServiceCaseReadOnly}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-checkinHandover'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.documents,
      label: i18n.t('general.labels.documents'),
      content: (
        <ServiceCaseDocuments
          maximization={maximization}
          vehicleId={props.serviceCase?.vehicleId}
          isReadOnly={isServiceCaseReadOnly}
        />
      ),
      badge: documentCount?.count
        ? {value: documentCount.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-documents'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.photos,
      label: i18n.t('entity.photo.labels.photos'),
      content: <Photos isReadOnly={isServiceCaseReadOnly} />,
      hasSeparator: true,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-photos'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: workshopSearchParams.serviceDetail.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      content: (
        <Section>
          <Checklists
            recordId={props.serviceCaseId}
            resourceId="SERVICE_CASE"
            contextTarget="service-case"
            contextId={props.serviceCaseId}
            isReadOnly={isServiceCaseReadOnly}
          />
        </Section>
      ),
      badge: checklistBadge,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-checklists'),
    })
    .when(props.canReadTasks, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          maximization={maximization}
          gridCode="service-case-task"
          entityId={props.serviceCaseId}
          isReadOnly={isServiceCaseReadOnly}
          autoHeight={false}
          data-testid={testIds.workshop.serviceCaseDetail('tasks')}
        />
      ),
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: workshopSearchParams.serviceDetail.customNotices,
      label: i18n.t('general.customNotices.labels.title'),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.serviceCase}
          recordId={props.serviceCaseId}
          data-testid={testIds.workshop.serviceCaseDetail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      hasSeparator: true,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-customNotices'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.vehicle,
      label: i18n.t('entity.vehicle.labels.vehicle'),
      content: <Vehicle maximization={maximization} />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-vehicle'),
    })
    .when(props.canReadServiceCase, {
      id: workshopSearchParams.serviceDetail.ownership,
      label: i18n.t('entity.serviceCase.labels.ownership'),
      content: (
        <OwnershipSection
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          resourceId={EntityResourceIds.serviceCase}
          recordId={props.serviceCaseId}
          additionalTab={{
            queryId: 'gdprConsents',
            title: i18n.t('entity.customer.labels.GDPRConsents'),
            content: (
              <GDPRCollector
                maximization={maximization}
                vehicleId={props.serviceCase?.vehicleId ?? ''}
              />
            ),
          }}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: props.vehicleCustomersCount ?? 0,
      },
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-ownership'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.postponed,
      label: i18n.t('entity.serviceCase.labels.postponed'),
      content: <Postponed />,
      badge: isPositive(props.postponedJobs?.count)
        ? {value: String(props.postponedJobs?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      isDisabled: isNonPositive(props.postponedJobs?.count),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-postponed'),
    })
    .when(props.canReadPneuhotel && isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.tires,
      label: i18n.t('page.tiresInventory.labels.tires'),
      badge: isNotNil(props.tireSetCount)
        ? {value: String(props.tireSetCount), colorScheme: 'neutral'}
        : undefined,
      content: (
        <TiresOverview
          maximization={maximization}
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          customerId={props.serviceCase?.customerId ?? ''}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-tires'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.serviceIntervals,
      label: i18n.t('entity.vehicle.labels.serviceIntervals'),
      content: (
        <ServiceIntervals
          vehicleId={props.serviceCase?.vehicleId}
          serviceCaseId={props.serviceCase?.id}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-serviceIntervals'),
    })
    .when(isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.activeServiceCases,
      label: i18n.t('entity.serviceCase.labels.activeServiceCases'),
      content: (
        <ActiveServiceCases
          maximization={maximization}
          vehicleId={props.serviceCase?.vehicleId ?? ''}
        />
      ),
      badge: isPositive(activeServiceCasesCount?.count)
        ? {value: String(activeServiceCasesCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-activeServiceCases'),
    })
    .when(isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.historyOfRepairs,
      label: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
      content: (
        <HistoryOfRepairs
          maximization={maximization}
          vehicleId={props.serviceCase?.vehicleId ?? ''}
        />
      ),
      badge: isPositive(finishedServiceCasesCount?.count)
        ? {value: String(finishedServiceCasesCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-historyOfRepairs'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.serviceCampaigns,
      label: i18n.t('entity.campaigns.labels.serviceCampaigns'),
      content: (
        <VehicleCampaigns
          vehicleId={props.serviceCase?.vehicleId}
          branchId={props.serviceCase?.branchId}
        />
      ),
      badge: isPositive(props.activeServiceCampaignsLength)
        ? {colorScheme: 'neutral', value: props.activeServiceCampaignsLength}
        : undefined,
      isDisabled: isNonPositive(props.activeServiceCampaignsLength),
      hasSeparator: true,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-serviceCampaigns'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.activityLogs,
      label: i18n.t('general.labels.activityLogs'),
      content: <ActivityLogs maximization={maximization} />,
      badge: {value: activityLogsCount ?? 0, colorScheme: 'neutral'},
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-activityLogs'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.communication,
      label: getCommunicationSectionTitle(),
      content: (
        <CommunicationSection
          maximization={maximization}
          customerId={props.serviceCase?.customerId ?? ''}
          recordId={props.serviceCaseId}
          resourceId={EntityResourceIds.serviceCase}
          data-testid="service-case-communication"
        />
      ),
      badge: {value: outgoingMessagesCount, colorScheme: 'neutral'},
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-communication'),
    });
}
